import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Shell = ({ children }) => (
  <div className="relative z-10 flex min-h-screen items-center justify-center px-5 py-20">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="glass glow-cyan w-full max-w-md rounded-3xl p-10 text-center"
    >
      {children}
    </motion.div>
  </div>
);

export function PaymentSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [tries, setTries] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) {
      setState("error");
      return;
    }
    let active = true;
    const poll = async (n) => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refreshUser?.();
          return setState("paid");
        }
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch {
        return setState("error");
      }
      if (n >= 8) return setState("pending");
      setTries(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { active = false; };
  }, []);

  return (
    <Shell>
      {state === "checking" && (
        <>
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-cyber-cyan" />
          <p className="mt-6 font-mono-tech text-xs uppercase tracking-[0.2em] text-white/60" data-testid="payment-checking">
            Confirmando pago... ({tries})
          </p>
        </>
      )}
      {state === "paid" && (
        <div data-testid="payment-paid">
          <CheckCircle2 className="mx-auto h-14 w-14 text-[#22e07a]" />
          <h1 className="mt-6 font-head text-3xl font-bold text-white">¡Pago confirmado!</h1>
          <p className="mt-3 font-sans text-sm text-white/60">
            Gracias por confiar en CYVR Labs. Nos pondremos en contacto contigo en menos de 24h para agendar tu análisis.
          </p>
          <button onClick={() => navigate("/panel")} data-testid="payment-goto-panel"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-cyber-cyan px-7 py-3.5 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#04060a] transition-transform duration-300 hover:scale-105">
            Ir a mi panel <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      )}
      {state === "pending" && (
        <>
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-cyber-cyan" />
          <h1 className="mt-6 font-head text-2xl font-bold text-white">Procesando</h1>
          <p className="mt-3 font-sans text-sm text-white/60">Tu pago se está procesando. Recibirás confirmación por email.</p>
        </>
      )}
      {state === "error" && (
        <>
          <XCircle className="mx-auto h-14 w-14 text-[#ff2d6f]" />
          <h1 className="mt-6 font-head text-2xl font-bold text-white">No pudimos confirmar el pago</h1>
          <p className="mt-3 font-sans text-sm text-white/60">Si el cargo se realizó, contáctanos en contacto@cyvrlabs.com.</p>
          <button onClick={() => navigate("/")} className="mt-8 rounded-full border border-white/15 px-7 py-3.5 font-mono-tech text-xs uppercase tracking-widest text-white/80 hover:border-cyber-cyan hover:text-cyber-cyan">
            Volver al inicio
          </button>
        </>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <Shell>
      <div data-testid="payment-cancel">
        <XCircle className="mx-auto h-14 w-14 text-white/40" />
        <h1 className="mt-6 font-head text-2xl font-bold text-white">Pago cancelado</h1>
        <p className="mt-3 font-sans text-sm text-white/60">No se ha realizado ningún cargo. Puedes retomarlo cuando quieras.</p>
        <button onClick={() => navigate("/#precios")} className="mt-8 rounded-full bg-cyber-cyan px-7 py-3.5 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#04060a] hover:scale-105 transition-transform">
          Ver tarifas
        </button>
      </div>
    </Shell>
  );
}
